import React, { useState, useEffect, useRef, useMemo, useCallback, useReducer } from 'react';
import { apiGet } from './apiUtils';
import { getCurrentUser } from './authUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Alert, AlertDescription } from './components/ui/alert';
import { Checkbox } from './components/ui/checkbox';
import { Label } from './components/ui/label';
import { Search, Plus, Trash2, FileDown, Calculator, Info, CheckCircle2 } from 'lucide-react';

// Constants
const SEARCH_DEBOUNCE_DELAY = 150;
const MIN_SEARCH_LENGTH = 1;
const TAX_RATES = {
  TCS_RATE: 0.01,
  RETAIL_EXCISE_RATE: 0.10,
};
const DEFAULT_PACK_QUANTITY = 12;

// State management using reducer
const initialState = {
  searchTerm: '',
  searchResults: [],
  selectedItems: [],
  loading: false,
  showResults: false,
  showEstimate: false,
  tenTimesCompleted: false,
  notification: null,
  searchCache: new Map(),
};

const actionTypes = {
  SET_SEARCH_TERM: 'SET_SEARCH_TERM',
  SET_SEARCH_RESULTS: 'SET_SEARCH_RESULTS',
  SET_LOADING: 'SET_LOADING',
  SET_SHOW_RESULTS: 'SET_SHOW_RESULTS',
  SET_SHOW_ESTIMATE: 'SET_SHOW_ESTIMATE',
  SET_TEN_TIMES_COMPLETED: 'SET_TEN_TIMES_COMPLETED',
  ADD_ITEM: 'ADD_ITEM',
  UPDATE_ITEM_QUANTITY: 'UPDATE_ITEM_QUANTITY',
  REMOVE_ITEM: 'REMOVE_ITEM',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION',
  CACHE_SEARCH_RESULT: 'CACHE_SEARCH_RESULT',
};

function stateReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_SEARCH_TERM:
      return { ...state, searchTerm: action.payload };
    case actionTypes.SET_SEARCH_RESULTS:
      return { ...state, searchResults: action.payload };
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    case actionTypes.SET_SHOW_RESULTS:
      return { ...state, showResults: action.payload };
    case actionTypes.SET_SHOW_ESTIMATE:
      return { ...state, showEstimate: action.payload };
    case actionTypes.SET_TEN_TIMES_COMPLETED:
      return { ...state, tenTimesCompleted: action.payload };
    case actionTypes.ADD_ITEM:
      return { ...state, selectedItems: [...state.selectedItems, action.payload] };
    case actionTypes.UPDATE_ITEM_QUANTITY:
      return {
        ...state,
        selectedItems: state.selectedItems.map(item =>
          item.id === action.payload.id
            ? { ...item, ...action.payload.updates }
            : item
        )
      };
    case actionTypes.REMOVE_ITEM:
      return {
        ...state,
        selectedItems: state.selectedItems.filter(item => item.id !== action.payload)
      };
    case actionTypes.SET_NOTIFICATION:
      return { ...state, notification: action.payload };
    case actionTypes.CLEAR_NOTIFICATION:
      return { ...state, notification: null };
    case actionTypes.CACHE_SEARCH_RESULT:
      const newCache = new Map(state.searchCache);
      newCache.set(action.payload.term, action.payload.results);
      return { ...state, searchCache: newCache };
    default:
      return state;
  }
}

function IndentEstimate() {
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const searchContainerRef = useRef(null);
  const summaryRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const notificationTimeoutRef = useRef(null);
  const [summaryPulse, setSummaryPulse] = useState(false);

  const user = getCurrentUser();
  const shopName = user.shopName || 'Easy Sheets, Daily';

  // Utility functions
  const calculateTotalBottles = useCallback((cases = 0, bottles = 0, packQuantity = DEFAULT_PACK_QUANTITY) => {
    return (cases * packQuantity) + bottles;
  }, []);

  const calculateItemAmount = useCallback((cases = 0, bottles = 0, invoicePrice = 0, packQuantity = DEFAULT_PACK_QUANTITY) => {
    const totalBottles = calculateTotalBottles(cases, bottles, packQuantity);
    return totalBottles * invoicePrice;
  }, [calculateTotalBottles]);

  const validateQuantity = useCallback((value, min = 0, max = 9999) => {
    const numValue = parseInt(value) || 0;
    return Math.max(min, Math.min(max, numValue));
  }, []);

  const showNotification = useCallback((message, type = 'info') => {
    dispatch({ type: actionTypes.SET_NOTIFICATION, payload: { message, type } });
    
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    
    notificationTimeoutRef.current = setTimeout(() => {
      dispatch({ type: actionTypes.CLEAR_NOTIFICATION });
    }, 3000);
  }, []);

  const createItemFromBrand = useCallback((brand) => {
    const invoicePrice = parseFloat(brand.invoice) || 0;
    const packQuantity = brand.pack_quantity || DEFAULT_PACK_QUANTITY;
    const cases = 1;
    const bottles = 0;
    
    return {
      id: brand.id,
      brandName: brand.brand_name,
      brandNumber: brand.brand_number,
      size: `${packQuantity}/${brand.size_ml}ml`,
      sizeCode: brand.size_code,
      cases,
      bottles,
      packQuantity,
      invoicePrice,
      specialMargin: parseFloat(brand.special_margin) || 0,
      specialExciseCess: parseFloat(brand.special_excise_cess) || 0,
      amount: calculateItemAmount(cases, bottles, invoicePrice, packQuantity)
    };
  }, [calculateItemAmount]);

  const handleSearch = useCallback(async (term) => {
    const searchQuery = (term || state.searchTerm).trim();
    
    if (!searchQuery || searchQuery.length < MIN_SEARCH_LENGTH) {
      dispatch({ type: actionTypes.SET_SEARCH_RESULTS, payload: [] });
      dispatch({ type: actionTypes.SET_SHOW_RESULTS, payload: false });
      return;
    }

    // Check cache first
    if (state.searchCache.has(searchQuery)) {
      const cachedResults = state.searchCache.get(searchQuery);
      dispatch({ type: actionTypes.SET_SEARCH_RESULTS, payload: cachedResults });
      dispatch({ type: actionTypes.SET_SHOW_RESULTS, payload: true });
      return;
    }
    
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    dispatch({ type: actionTypes.SET_LOADING, payload: true });
    
    try {
      const response = await apiGet(`/api/search-brands?q=${encodeURIComponent(searchQuery)}`);
      const data = await response.json();
      // Support both shapes: { brands: [...] } or [...]
      const results = Array.isArray(data) ? data : (data.brands || []);
        
        dispatch({ type: actionTypes.SET_SEARCH_RESULTS, payload: results });
        dispatch({ type: actionTypes.SET_SHOW_RESULTS, payload: true });
        dispatch({ type: actionTypes.CACHE_SEARCH_RESULT, payload: { term: searchQuery, results } });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
        showNotification('Failed to search products. Please try again.', 'error');
        dispatch({ type: actionTypes.SET_SEARCH_RESULTS, payload: [] });
        dispatch({ type: actionTypes.SET_SHOW_RESULTS, payload: false });
      }
    } finally {
      dispatch({ type: actionTypes.SET_LOADING, payload: false });
    }
  }, [state.searchTerm, state.searchCache, showNotification]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value.slice(0, 100); // Limit input length
    dispatch({ type: actionTypes.SET_SEARCH_TERM, payload: value });
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for real-time search
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(value);
    }, SEARCH_DEBOUNCE_DELAY);
  }, [handleSearch]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      handleSearch();
    } else if (e.key === 'Escape') {
      dispatch({ type: actionTypes.SET_SHOW_RESULTS, payload: false });
      dispatch({ type: actionTypes.SET_SEARCH_TERM, payload: '' });
    }
  }, [handleSearch]);

  // Handle click outside to close results and cleanup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        dispatch({ type: actionTypes.SET_SHOW_RESULTS, payload: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      // Cleanup all timeouts and listeners
      document.removeEventListener('mousedown', handleClickOutside);
      
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
      
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const addToEstimate = useCallback((brand) => {
    const existingItem = state.selectedItems.find(item => item.id === brand.id);
    if (existingItem) {
      return;
    }

    const newItem = createItemFromBrand(brand);
    dispatch({ type: actionTypes.ADD_ITEM, payload: newItem });
    // Keep search open and term intact so user can add multiple items quickly
  }, [state.selectedItems, createItemFromBrand]);

  const updateQuantity = useCallback((id, field, value) => {
    const item = state.selectedItems.find(item => item.id === id);
    if (!item) return;
    
    let validatedValue;
    if (field === 'cases') {
      validatedValue = validateQuantity(value, 0, 9999);
    } else if (field === 'bottles') {
      validatedValue = validateQuantity(value, 0, item.packQuantity - 1);
    } else {
      return;
    }
    
    const updates = { [field]: validatedValue };
    
    // Recalculate amount
    const newCases = field === 'cases' ? validatedValue : item.cases;
    const newBottles = field === 'bottles' ? validatedValue : item.bottles;
    updates.amount = calculateItemAmount(newCases, newBottles, item.invoicePrice, item.packQuantity);
    
    dispatch({ type: actionTypes.UPDATE_ITEM_QUANTITY, payload: { id, updates } });
  }, [state.selectedItems, validateQuantity, calculateItemAmount]);

  const removeItem = useCallback((id) => {
    dispatch({ type: actionTypes.REMOVE_ITEM, payload: id });
    showNotification('Item removed from estimate', 'info');
  }, [showNotification]);

  const handleShowEstimate = useCallback(() => {
    dispatch({ type: actionTypes.SET_SHOW_ESTIMATE, payload: true });
    showNotification('Estimate summary generated', 'success');
    // Scroll and highlight
    requestAnimationFrame(() => {
      if (summaryRef.current) {
        summaryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setSummaryPulse(true);
        setTimeout(() => setSummaryPulse(false), 1500);
      }
    });
  }, [showNotification]);

  // Memoized calculations
  const estimate = useMemo(() => {
    const invoiceValue = state.selectedItems.reduce((total, item) => total + (item.amount || 0), 0);
    
    const mrpRoundingOff = state.selectedItems.reduce((total, item) => {
      const itemTotalBottles = calculateTotalBottles(item.cases, item.bottles, item.packQuantity);
      return total + (itemTotalBottles * (item.specialMargin || 0));
    }, 0);
    
    const netInvoiceValue = invoiceValue + mrpRoundingOff;
    const retailExciseTurnoverTax = state.tenTimesCompleted ? invoiceValue * TAX_RATES.RETAIL_EXCISE_RATE : 0;
    
    const specialExciseCess = state.selectedItems.reduce((total, item) => {
      const itemTotalBottles = calculateTotalBottles(item.cases, item.bottles, item.packQuantity);
      return total + (itemTotalBottles * (item.specialExciseCess || 0));
    }, 0);
    
    // TCS = 1% of (Invoice + MRP Rounding Off + Retail Excise Turnover Tax)
    const tcsBase = netInvoiceValue + retailExciseTurnoverTax;
    const tcs = tcsBase * TAX_RATES.TCS_RATE;
    const grandTotal = netInvoiceValue + retailExciseTurnoverTax + specialExciseCess + tcs;

    return {
      invoiceValue,
      mrpRoundingOff,
      netInvoiceValue,
      retailExciseTurnoverTax,
      specialExciseCess,
      tcs,
      grandTotal
    };
  }, [state.selectedItems, state.tenTimesCompleted, calculateTotalBottles]);

  // Utility function to format numbers with Indian comma system
  const formatIndianCurrency = useCallback((amount) => {
    const num = parseFloat(amount || 0);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  }, []);

  // Clean PDF generation utility function - Print-to-PDF HTML approach
  const generatePDF = useCallback((items, totalAmount, estimateData = null) => {
    if (!items || items.length === 0) {
      showNotification('Please add items to the estimate before generating PDF', 'warning');
      return;
    }

    const formatINR = (amount) => {
      const num = parseFloat(amount || 0);
      return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const rowsHtml = items.map((item, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${item.brandName}</td>
        <td>${item.size}</td>
        <td>${item.cases}</td>
        <td>${item.bottles}</td>
        <td>${formatINR(item.invoicePrice || 0)}</td>
        <td class="text-right">${formatINR(item.amount || 0)}</td>
      </tr>
    `).join('');

    const summaryHtml = estimateData ? `
      <h3 style="margin-top: 24px; text-align: center;">Estimate Summary</h3>
      <table style="width:100%; border-collapse: collapse;">
        <tbody>
          <tr><td style="padding:8px; border:1px solid #ddd;">Invoice Value:</td><td style="padding:8px; border:1px solid #ddd; text-align:right;">${formatINR(estimateData.invoiceValue)}</td></tr>
          <tr><td style="padding:8px; border:1px solid #ddd;">MRP Rounding Off:</td><td style="padding:8px; border:1px solid #ddd; text-align:right;">${formatINR(estimateData.mrpRoundingOff)}</td></tr>
          <tr><td style="padding:8px; border:1px solid #ddd;">Net Invoice Value:</td><td style="padding:8px; border:1px solid #ddd; text-align:right; font-weight:700;">${formatINR(estimateData.netInvoiceValue)}</td></tr>
          <tr><td style="padding:8px; border:1px solid #ddd;">Retail Excise Turnover Tax:</td><td style="padding:8px; border:1px solid #ddd; text-align:right;">${formatINR(estimateData.retailExciseTurnoverTax)}</td></tr>
          <tr><td style="padding:8px; border:1px solid #ddd;">Special Excise Cess:</td><td style="padding:8px; border:1px solid #ddd; text-align:right;">${formatINR(estimateData.specialExciseCess)}</td></tr>
          <tr><td style="padding:8px; border:1px solid #ddd;">TCS:</td><td style="padding:8px; border:1px solid #ddd; text-align:right;">${formatINR(estimateData.tcs)}</td></tr>
          <tr><td style="padding:8px; border:1px solid #ddd;">Grand Total:</td><td style="padding:8px; border:1px solid #ddd; text-align:right; font-weight:700;">${formatINR(estimateData.grandTotal)}</td></tr>
        </tbody>
      </table>
    ` : '';

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Indent Estimate</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .company-name { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
          .document-title { font-size: 18px; color: #666; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #f5f5f5; font-weight: bold; }
          .text-right { text-align: right; }
          .total-row { background-color: #f0f0f0; font-weight: bold; }
          .footer { margin-top: 30px; text-align: center; color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${shopName}</div>
          <div class="document-title">Indent Estimate</div>
          <div style="margin-top: 10px; color: #666;">Generated on: ${new Date().toLocaleDateString('en-IN')}</div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Brand Name</th>
              <th>Size</th>
              <th>Cases</th>
              <th>Bottles</th>
              <th>Invoice Price/Bottle</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
            <tr class="total-row">
              <td colspan="6" class="text-right">Invoice Value:</td>
              <td class="text-right">${formatINR(totalAmount)}</td>
            </tr>
          </tbody>
        </table>

        ${summaryHtml}
        
        <div class="footer">
          <p>This is computer generated estimate, actuals may slightly vary.</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      };
      showNotification('Print dialog opened. Save as PDF to download.', 'info');
    } else {
      showNotification('Please allow popups to generate PDF', 'warning');
    }
  }, [showNotification, shopName]);

  // Wrapper function to maintain compatibility
  const handleGeneratePDF = useCallback(() => {
    const estimateData = state.showEstimate ? estimate : null;
    generatePDF(state.selectedItems, estimate.invoiceValue, estimateData);
  }, [generatePDF, state.selectedItems, state.showEstimate, estimate]);

  // Notification component
  const NotificationToast = ({ notification, onClose }) => {
    if (!notification) return null;
    
    const { message, type } = notification;
    const variantMap = {
      success: 'default',
      error: 'destructive',
      warning: 'default',
      info: 'default'
    };
    const iconMap = {
      success: <CheckCircle2 className="h-4 w-4 text-green-600" />,
      error: <Info className="h-4 w-4" />,
      warning: <Info className="h-4 w-4 text-yellow-600" />,
      info: <Info className="h-4 w-4" />
    };
    
    return (
      <div className="fixed top-20 right-4 z-50 w-96 animate-in slide-in-from-top-2">
        <Alert variant={variantMap[type]} className={type === 'success' ? 'border-green-600' : type === 'warning' ? 'border-yellow-600' : ''}>
          {iconMap[type]}
          <AlertDescription className="ml-2 flex items-center justify-between">
            <span>{message}</span>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">×</Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  };

  return (
    <>
      {/* Notification Toast */}
      <NotificationToast 
        notification={state.notification} 
        onClose={() => dispatch({ type: actionTypes.CLEAR_NOTIFICATION })} 
      />
      
      <main className="w-full px-4 py-8 space-y-8 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="space-y-2 text-left">
          <h1 className="text-4xl font-bold text-left">Indent Estimate</h1>
          <p className="text-base text-muted-foreground text-left">Create and estimate your indent orders</p>
        </div>

        {/* Product Search Section */}
        <Card ref={searchContainerRef}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-left">
              <Search className="h-6 w-6" />
              Product Search
            </CardTitle>
            <CardDescription className="text-left">Find products to add to your estimate</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type="text"
                  placeholder="Search by brand number or brand name..."
                  value={state.searchTerm}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyPress}
                  onFocus={() => {
                    if (state.searchResults.length > 0 && state.searchTerm.length >= MIN_SEARCH_LENGTH) {
                      dispatch({ type: actionTypes.SET_SHOW_RESULTS, payload: true });
                    }
                  }}
                  aria-label="Search products"
                  aria-autocomplete="list"
                  className="pr-10"
                />
                <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              </div>
              <Button 
                onClick={() => handleSearch()}
                disabled={state.loading}
                aria-label="Search products"
              >
                {state.loading ? 'Searching...' : 'Search'}
              </Button>
            </div>

            {/* Search Results */}
            {state.showResults && (
              <div className="mt-4 border rounded-lg overflow-hidden max-h-96 overflow-y-auto" role="listbox" aria-label="Search results">
                {state.loading ? (
                  <div className="flex items-center justify-center p-8" role="status" aria-live="polite">
                    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mr-3"></div>
                    <span>Searching...</span>
                  </div>
                ) : state.searchResults.length > 0 ? (
                  <div className="divide-y">
                    {state.searchResults.map((brand) => (
                      <div 
                        key={brand.id} 
                        className="p-4 hover:bg-accent cursor-pointer flex items-center justify-between group transition-colors" 
                        onClick={() => addToEstimate(brand)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            addToEstimate(brand);
                          }
                        }}
                        tabIndex={0}
                        role="option"
                        aria-selected={false}
                      >
                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-3 mb-1">
                            <Badge variant="outline" className="font-mono">{brand.brand_number}</Badge>
                            <Badge variant="secondary">{brand.pack_type || 'G'}</Badge>
                            <Badge variant="secondary">{brand.pack_quantity || DEFAULT_PACK_QUANTITY}/{brand.size_ml}ml</Badge>
                          </div>
                          <p className="font-medium text-left">{brand.brand_name}</p>
                        </div>
                        <Button 
                          size="sm"
                          variant={state.selectedItems.some(item => item.id === brand.id) ? "outline" : "default"}
                          onClick={(e) => {
                            e.stopPropagation();
                            addToEstimate(brand);
                          }}
                          aria-label={`Add ${brand.brand_name} to estimate`}
                          disabled={state.selectedItems.some(item => item.id === brand.id)}
                        >
                          {state.selectedItems.some(item => item.id === brand.id) ? (
                            <>
                              <CheckCircle2 className="mr-1 h-4 w-4" />
                              Added
                            </>
                          ) : (
                            <>
                              <Plus className="mr-1 h-4 w-4" />
                              Add
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : state.searchTerm.length >= MIN_SEARCH_LENGTH ? (
                  <div className="text-center p-8 text-muted-foreground" role="status">No products found for "{state.searchTerm}"</div>
                ) : (
                  <div className="text-center p-8 text-muted-foreground" role="status">Type at least {MIN_SEARCH_LENGTH} character to search</div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Estimation Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between text-left">
              <span className="flex items-center gap-2">
                <Calculator className="h-6 w-6" />
                Estimation
              </span>
              {state.selectedItems.length > 0 && (
                <Badge variant="secondary">{state.selectedItems.length} items</Badge>
              )}
            </CardTitle>
            <CardDescription className="text-left">Add products and adjust quantities for your estimate</CardDescription>
          </CardHeader>
          <CardContent>
            {state.selectedItems.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Calculator className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-lg font-medium mb-2">No items added to estimate</p>
                <p className="text-sm text-muted-foreground">Use the search above to add products</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-md border overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px]">S.No</TableHead>
                        <TableHead>Brand Name</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead className="text-right">Cases</TableHead>
                        <TableHead className="text-right">Bottles</TableHead>
                        <TableHead className="text-right">Invoice Price</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="w-[100px]">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {state.selectedItems.map((item, index) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{index + 1}</TableCell>
                          <TableCell>{item.brandName}</TableCell>
                          <TableCell>{item.size}</TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min="0"
                              max="9999"
                              step="1"
                              value={item.cases === 0 ? '' : item.cases}
                              onChange={(e) => updateQuantity(item.id, 'cases', e.target.value)}
                              onWheel={(e) => e.currentTarget.blur()}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                  e.preventDefault();
                                }
                              }}
                              onBlur={(e) => {
                                const value = validateQuantity(e.target.value, 0, 9999);
                                if (value !== item.cases) {
                                  updateQuantity(item.id, 'cases', value);
                                }
                              }}
                              className="w-20 text-right"
                              placeholder="0"
                              onFocus={(e) => e.target.select()}
                              aria-label={`Cases for ${item.brandName}`}
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input
                              type="number"
                              min="0"
                              max={item.packQuantity - 1}
                              step="1"
                              value={item.bottles === 0 ? '' : item.bottles}
                              onChange={(e) => updateQuantity(item.id, 'bottles', e.target.value)}
                              onWheel={(e) => e.currentTarget.blur()}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                  e.preventDefault();
                                }
                              }}
                              onBlur={(e) => {
                                const value = validateQuantity(e.target.value, 0, item.packQuantity - 1);
                                if (value !== item.bottles) {
                                  updateQuantity(item.id, 'bottles', value);
                                }
                              }}
                              className="w-20 text-right"
                              placeholder="0"
                              onFocus={(e) => e.target.select()}
                              aria-label={`Bottles for ${item.brandName}`}
                            />
                          </TableCell>
                          <TableCell className="text-right">{formatIndianCurrency(item.invoicePrice || 0)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatIndianCurrency(item.amount || 0)}</TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(item.id)}
                              aria-label={`Remove ${item.brandName} from estimate`}
                              title="Remove item"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex justify-end">
                  <Button 
                    size="lg"
                    onClick={handleShowEstimate}
                    disabled={state.selectedItems.length === 0}
                    aria-label="Calculate estimate summary"
                  >
                    <Calculator className="mr-2 h-5 w-5" />
                    Get Estimate
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Estimate Summary Section */}
        {state.showEstimate && state.selectedItems.length > 0 && (
          <Card ref={summaryRef} className={summaryPulse ? 'animate-pulse' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-left">
                <FileDown className="h-6 w-6" />
                Estimate Summary
              </CardTitle>
              <CardDescription className="text-left">Review and download your indent estimate</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* 10 Times Checkbox */}
              <div className="flex items-center space-x-2 p-4 bg-muted rounded-lg">
                <Checkbox 
                  id="ten-times"
                  checked={state.tenTimesCompleted}
                  onCheckedChange={(checked) => dispatch({ type: actionTypes.SET_TEN_TIMES_COMPLETED, payload: checked })}
                />
                <Label htmlFor="ten-times" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer">
                  10 Times Stock Lifted Completed (10% Retail Excise Turnover Tax)
                </Label>
              </div>

              {/* Calculations */}
              <div className="space-y-3 p-4 border rounded-lg">
                <h4 className="font-semibold text-lg mb-4 text-left">Financial Breakdown</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <span className="text-muted-foreground text-left">Invoice Value:</span>
                  <span className="font-semibold text-right">{formatIndianCurrency(estimate.invoiceValue)}</span>
                  
                  <span className="text-muted-foreground text-left">MRP Rounding Off:</span>
                  <span className="font-semibold text-right">{formatIndianCurrency(estimate.mrpRoundingOff)}</span>
                  
                  <div className="col-span-2 border-t pt-3">
                    <div className="grid grid-cols-2">
                      <span className="font-bold text-left">Net Invoice Value:</span>
                      <span className="font-bold text-right text-primary">{formatIndianCurrency(estimate.netInvoiceValue)}</span>
                    </div>
                  </div>
                  
                  <span className="text-muted-foreground text-left">Retail Excise Turnover Tax:</span>
                  <span className="font-semibold text-right">{formatIndianCurrency(estimate.retailExciseTurnoverTax)}</span>
                  
                  <span className="text-muted-foreground text-left">Special Excise Cess:</span>
                  <span className="font-semibold text-right">{formatIndianCurrency(estimate.specialExciseCess)}</span>
                  
                  <span className="text-muted-foreground text-left">TCS:</span>
                  <span className="font-semibold text-right">{formatIndianCurrency(estimate.tcs)}</span>
                  
                  <div className="col-span-2 border-t pt-3 mt-3">
                    <div className="grid grid-cols-2">
                      <span className="font-bold text-lg text-left">Grand Total:</span>
                      <span className="font-bold text-lg text-right text-primary">{formatIndianCurrency(estimate.grandTotal)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* PDF Download Button */}
              <div className="flex justify-end">
                <Button 
                  size="lg"
                  onClick={handleGeneratePDF}
                  disabled={state.loading}
                  aria-label="Download estimate as PDF"
                >
                  {state.loading ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2"></div>
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <FileDown className="mr-2 h-5 w-5" />
                      Download Indent Estimate (PDF)
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </>
  );
}

export default IndentEstimate;